import React, { useState, useCallback } from 'react';
import { Search, User, X } from 'lucide-react';
import resumeAPI from '../services/api';
import { messagingAPI } from '../services/messaging';

/**
 * Modal to start a new 1:1 or group conversation. Search users, multiselect, optional group name.
 */
export default function NewConversationModal({ onClose, onCreated }) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    const q = (query || '').trim();
    if (!q) {
      setResults([]);
      return;
    }
    setSearching(true);
    setError('');
    try {
      const list = await resumeAPI.searchPublicProfiles(q, 25, { includeNonPublic: false, includeFull: false });
      setResults(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || 'Search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const toggle = useCallback((user) => {
    const uid = user.profile_user_id || user.user_id;
    if (!uid) return;
    setSelected((prev) => {
      const has = prev.some((u) => (u.profile_user_id || u.user_id) === uid);
      if (has) return prev.filter((u) => (u.profile_user_id || u.user_id) !== uid);
      return [...prev, user];
    });
  }, []);

  const isSelected = useCallback((user) => {
    const uid = user.profile_user_id || user.user_id;
    return selected.some((u) => (u.profile_user_id || u.user_id) === uid);
  }, [selected]);

  const handleStart = useCallback(async () => {
    if (selected.length === 0) return;
    setCreating(true);
    setError('');
    try {
      const participantIds = selected.map((u) => u.profile_user_id || u.user_id).filter(Boolean);
      const isGroup = participantIds.length > 1;
      const { conversation } = await messagingAPI.createConversation({
        participant_ids: participantIds,
        is_group: isGroup,
        group_name: isGroup ? (groupName || undefined) : undefined,
      });
      onCreated?.(conversation);
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  }, [selected, groupName, onCreated, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New message</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a name or multiple names"
                className="input w-full pl-9"
              />
            </div>
            <button type="submit" disabled={searching} className="btn btn-primary">
              {searching ? 'Searching...' : 'Search'}
            </button>
          </form>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
          {results.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Select people</p>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {results.map((r) => {
                  const uid = r.profile_user_id || r.user_id;
                  const name = r.title || r.name || 'Unknown';
                  const chosen = isSelected(r);
                  return (
                    <li key={uid}>
                      <button
                        type="button"
                        onClick={() => toggle(r)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg text-left ${
                          chosen ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50'
                        }`}
                      >
                        {r.photo_url ? (
                          <img src={r.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{name}</p>
                          {r.headline && <p className="text-xs text-gray-500 truncate">{r.headline}</p>}
                        </div>
                        <span className={`flex-shrink-0 w-4 h-4 rounded border ${chosen ? 'bg-primary-600 border-primary-600' : 'border-gray-300'}`} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {selected.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group name (optional)</label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g. Project team"
                className="input w-full"
              />
            </div>
          )}
          {selected.length > 0 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-gray-600">
                {selected.length} selected {selected.length > 1 ? '(group)' : ''}
              </p>
              <button
                type="button"
                onClick={handleStart}
                disabled={creating}
                className="btn btn-primary"
              >
                {creating ? 'Creating...' : 'Start conversation'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
